<template>
  <modal-vue
    :modal-active="modalActive"
    max-width="!w-[1050px] !max-w-[1050px]"
    :has-header-border="true"
    :has-footer-border="true"
    @close="handleAttemptClose"
  >
    <template #header>
      <div class="flex items-center justify-between w-full pr-8">
        <p class="font-bold text-lg text-textPrimary">Editar política de qualificação documental</p>
        <LabelBadge
          v-if="isDirty"
          label="Edições não salvas"
          color="orange"
          size="small"
          data-testid="edit-qualification-doc-unsaved-badge"
        />
      </div>
    </template>

    <div class="flex flex-col gap-2 p-4 flex-1 min-h-0">
      <EditableHeader
        v-model="policyName"
        placeholder="Nome da política"
        data-testid="edit-qualification-doc-policy-name"
        @save="markDirty"
      />

      <div class="flex flex-col gap-3">
        <p class="text-sm text-textPrimary text-left font-semibold">
          Vigência do vínculo entre escala e a política
        </p>
        <div class="flex flex-wrap items-center gap-4">
          <GenericInput
            v-model="startDate"
            type="date"
            label="Início da vigência"
            density="compact"
            hide-details
            class="max-w-[200px]"
            data-testid="edit-qualification-doc-start-date"
            @update:model-value="markDirty"
          />
          <GenericInput
            v-model="endDate"
            type="date"
            label="Fim da vigência"
            density="compact"
            hide-details
            :disabled="isIndefiniteEnd"
            class="max-w-[200px]"
            data-testid="edit-qualification-doc-end-date"
            @update:model-value="markDirty"
          />
          <DsSwitch
            v-model="isIndefiniteEnd"
            label="Fim de vigência indeterminado"
            data-testid="edit-qualification-doc-indefinite-toggle"
            @update:model-value="handleIndefiniteToggle"
          />
        </div>
      </div>

      <p class="text-xs text-[#666] text-left">
        Marque Sim ou Não para cada critério. O salvamento dispara avaliação de pós-plantão nos
        plantões pendentes deste médico.
      </p>

      <div class="flex flex-col md:flex-row items-start md:items-center gap-4">
        <div class="w-[327px] shrink-0" style="height: 40px">
          <SearchInput
            v-model="searchText"
            width="100%"
            label="Buscar médico"
            hide-details
            density="compact"
            :search-function="fetchDoctors"
            data-testid="edit-qualification-doc-search-doctor"
          />
        </div>
        <div class="ml-auto">
          <OutlineButton
            text="Adicionar médico"
            width="auto"
            height="32px"
            :prepend-icon="mdiChevronDown"
            class="text-sm !w-auto !px-4 !py-1"
            data-testid="edit-qualification-doc-add-doctor-button"
            @click="showAddDoctorModal = true"
          />
        </div>
      </div>

      <div class="flex items-center justify-between">
        <p class="text-sm text-gray-600" data-testid="edit-qualification-doc-doctor-count">
          {{ totalDoctors }} médicos encontrados
        </p>
        <Checkbox
          :checked="includeInvited"
          fieldId="edit-qualification-doc-include-invited"
          label="Incluir médicos convidados"
          data-testid="edit-qualification-doc-include-invited-checkbox"
          @update:checked="handleToggleInvited"
        />
      </div>

      <div class="overflow-auto flex-1 rounded-lg shadow-sm border border-gray-200 min-h-[300px]">
        <template v-if="isDoctorsLoading">
          <div class="animate-pulse space-y-4 p-6">
            <div v-for="i in 5" :key="i" class="flex items-center gap-4">
              <div class="h-9 w-9 bg-gray-200 rounded-full"></div>
              <div class="flex-1 space-y-2">
                <div class="h-4 bg-gray-200 rounded w-1/3"></div>
                <div class="h-3 bg-gray-200 rounded w-1/5"></div>
              </div>
              <div class="h-6 w-12 bg-gray-200 rounded-full"></div>
              <div class="h-6 w-12 bg-gray-200 rounded-full"></div>
              <div class="h-6 w-12 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        </template>

        <template v-else>
          <TableModular
            v-if="doctors.length > 0"
            :rows="doctors"
            :columns="doctorColumns"
            actions-label="AÇÕES"
          >
            <template #column-name="{ row }">
              <DoctorCell :doctor="row as QualificationDocumentalDoctor" />
            </template>

            <template #column-hasRegistrationForm="{ row }">
              <div class="flex items-center gap-2">
                <LabelBadge
                  :label="row.hasRegistrationForm ? 'Sim' : 'Não'"
                  :color="row.hasRegistrationForm ? 'green' : 'red'"
                  size="small"
                  :icon="null"
                />
                <DsSwitch
                  :model-value="row.hasRegistrationForm"
                  size="small"
                  :data-testid="`edit-qualification-doc-toggle-registration-form-${row.id}`"
                  @update:model-value="(val: boolean) => handleToggleRegistrationForm(row.id, val)"
                />
              </div>
            </template>

            <template #column-hasContract="{ row }">
              <div class="flex items-center gap-2">
                <LabelBadge
                  :label="row.hasContract ? 'Sim' : 'Não'"
                  :color="row.hasContract ? 'green' : 'red'"
                  size="small"
                  :icon="null"
                />
                <DsSwitch
                  :model-value="row.hasContract"
                  size="small"
                  :data-testid="`edit-qualification-doc-toggle-contract-${row.id}`"
                  @update:model-value="(val: boolean) => handleToggleContract(row.id, val)"
                />
              </div>
            </template>

            <template #column-hasDocumentation="{ row }">
              <div class="flex items-center gap-2">
                <LabelBadge
                  :label="row.hasDocumentation ? 'Sim' : 'Não'"
                  :color="row.hasDocumentation ? 'green' : 'red'"
                  size="small"
                  :icon="null"
                />
                <DsSwitch
                  :model-value="row.hasDocumentation"
                  size="small"
                  :data-testid="`edit-qualification-doc-toggle-documentation-${row.id}`"
                  @update:model-value="(val: boolean) => handleToggleDocumentation(row.id, val)"
                />
              </div>
            </template>

            <template #actions="{ row }">
              <button
                class="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                :data-testid="`edit-qualification-doc-remove-doctor-button-${row.id}`"
                @click="handleRemoveDoctor(row.id)"
              >
                <v-icon :icon="mdiTrashCanOutline" size="20" />
              </button>
            </template>
          </TableModular>

          <EmptyList v-else title="Nenhum médico encontrado" class="py-12" />
        </template>
      </div>
    </div>

    <template #footer>
      <div class="flex gap-6 w-full">
        <OutlineButton
          class="flex-1"
          text="Cancelar"
          height="48px"
          data-testid="edit-qualification-doc-cancel-button"
          @click="handleAttemptClose"
        />
        <FilledButton
          class="flex-1"
          color="primary30"
          text="Salvar"
          height="48px"
          :loading="isSaving"
          data-testid="edit-qualification-doc-save-button"
          @click="handleSave"
        />
      </div>
    </template>
  </modal-vue>

  <modal-vue
    :modal-active="showDiscardConfirm"
    is-filter-modal
    :show-close-button="false"
    :has-footer-border="false"
    @close="cancelDiscard"
  >
    <div class="flex flex-col gap-4 py-4 px-2">
      <div class="flex items-center gap-2">
        <div
          class="flex items-center justify-center shrink-0"
          style="
            width: 32px;
            height: 32px;
            border-radius: 100px;
            border: 1px solid #f1c21b;
            background: #fef9e7;
          "
        >
          <span class="text-warning font-bold text-sm leading-none">!</span>
        </div>
        <h3 class="font-bold text-lg text-textPrimary" data-testid="discard-changes-doc-title">
          Descartar alterações?
        </h3>
      </div>
      <p class="text-sm text-gray-600" data-testid="discard-changes-doc-description">
        Você tem alterações não salvas. Ao sair, as alterações serão descartadas e esse processo não
        poderá ser revertido.
      </p>
    </div>

    <template #footer>
      <div class="flex gap-4 w-full">
        <OutlineButton
          class="flex-1"
          text="Descartar alterações"
          height="48px"
          data-testid="discard-changes-doc-confirm-button"
          @click="confirmDiscard"
        />
        <FilledButton
          class="flex-1"
          color="primary30"
          text="Voltar"
          height="48px"
          data-testid="discard-changes-doc-cancel-button"
          @click="cancelDiscard"
        />
      </div>
    </template>
  </modal-vue>

  <AddDoctorModal
    v-if="props.policy"
    :modal-active="showAddDoctorModal"
    :policy-id="props.policy?.id ?? 0"
    @close="showAddDoctorModal = false"
    @add="handleConfirmAddDoctors"
  />

  <ConfirmDeleteModal
    :show="showRemoveConfirm"
    title="Confirmar remoção"
    description="Tem certeza que deseja remover este médico da política?"
    text-button="Remover"
    @close-modal="cancelRemoveDoctor"
    @handle-confirm="confirmRemoveDoctor"
  />
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { mdiChevronDown, mdiTrashCanOutline } from '@mdi/js'
import { useToast } from 'vue-toastification'

import { useQualificationDocumentalPolicyStore } from '@/pinia-store/useQualificationDocumentalPolicyStore'
import { useErrorToast } from '@/composables/useErrorToast'
import Network from '@/Network'
import type { ToggleDoctorFieldRequest } from '@/Network/Types/Requests/QualificationDocumentalRequests'
import type {
  QualificationDocumentalPolicyItem,
  QualificationDocumentalDoctor
} from '@/Network/Types/Responses/QualificationDocumentalResponses'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import EditableHeader from '@/components/designSystem/Inputs/EditableHeader.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import Checkbox from '@/components/designSystem/Checkbox/index.vue'
import TableModular from '@/components/designSystem/Table/TableModular/TableModular.vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import EmptyList from '@/components/EmptyList.vue'
import DoctorCell from '@/modules/company/Policy/QualificationDocumental/components/DoctorCell.vue'
import AddDoctorModal from '@/modules/company/Policy/QualificationDocumental/components/AddDoctorModal.vue'
import ConfirmDeleteModal from '@/components/designSystem/modals/confirm.delete.action.vue'

const props = defineProps<{
  modalActive: boolean
  policy?: QualificationDocumentalPolicyItem
  scheduleId?: number
  linkId?: number
}>()

const emit = defineEmits<{
  close: []
  saved: []
}>()

// `toast` fica apenas para o sucesso e para as validações locais do formulário;
// todo erro vindo da API passa pelo `notifyError` (rule `error-handling.md`).
const toast = useToast()
const { notifyError } = useErrorToast()
const store = useQualificationDocumentalPolicyStore()
const { doctors, totalDoctors, isDoctorsLoading } = storeToRefs(store)

const isSaving = ref(false)
const isDirty = ref(false)

const policyName = ref('')
const startDate = ref('')
const endDate = ref('')
const isIndefiniteEnd = ref(false)
const searchText = ref('')
const includeInvited = ref(false)
const showAddDoctorModal = ref(false)
const showRemoveConfirm = ref(false)
const doctorToRemove = ref<number | null>(null)
const showDiscardConfirm = ref(false)

const doctorColumns = [
  { key: 'name', label: 'MÉDICO' },
  { key: 'hasRegistrationForm', label: 'FORMULÁRIO DE CADASTRO' },
  { key: 'hasContract', label: 'CONTRATO/PROCURAÇÃO' },
  { key: 'hasDocumentation', label: 'DOCUMENTAÇÃO' }
]

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return ''
  return iso.includes('T') ? iso.split('T')[0] : iso
}

function initFormFromPolicy(policy: QualificationDocumentalPolicyItem) {
  policyName.value = policy.policyName
  startDate.value = toDateInputValue(policy.startDate)
  endDate.value = toDateInputValue(policy.endDate)
  isIndefiniteEnd.value = !policy.endDate
  isDirty.value = false
  showDiscardConfirm.value = false
}

watch(
  () => props.modalActive,
  (active) => {
    if (active && props.policy) {
      initFormFromPolicy(props.policy)
      fetchDoctors()
    } else {
      store.$reset()
    }
  }
)

function markDirty() {
  isDirty.value = true
}

function handleAttemptClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true
  } else {
    emit('close')
  }
}

function confirmDiscard() {
  showDiscardConfirm.value = false
  emit('close')
}

function cancelDiscard() {
  showDiscardConfirm.value = false
}

function handleIndefiniteToggle(value: boolean) {
  if (value) {
    endDate.value = ''
  }
  markDirty()
}

async function fetchDoctors() {
  if (!props.policy) return
  try {
    await store.fetchDoctors(props.policy.id, {
      q: searchText.value || undefined,
      includeInvited: includeInvited.value
    })
  } catch (e) {
    notifyError(e, 'Erro ao carregar médicos.')
  }
}

// Na falha a modal fica aberta, com a seleção, para o usuário tentar de novo.
async function handleConfirmAddDoctors(doctorIds: number[]) {
  if (!props.policy) return
  try {
    await store.addDoctors(props.policy.id, doctorIds)
    showAddDoctorModal.value = false
  } catch (e) {
    notifyError(e, 'Erro ao adicionar médicos à política.')
  }
}

async function toggleField(policyId: number, params: ToggleDoctorFieldRequest) {
  try {
    await store.toggleDoctorField(policyId, params)
  } catch (e) {
    notifyError(e, 'Erro ao atualizar dados do médico.')
  }
}

function handleToggleInvited({ isChecked }: { isChecked: boolean }) {
  includeInvited.value = isChecked
  fetchDoctors()
}

function handleToggleRegistrationForm(doctorId: number, value: boolean) {
  if (!props.policy) return
  toggleField(props.policy.id, { doctorId, field: 'hasRegistrationForm', value })
  markDirty()
}

function handleToggleContract(doctorId: number, value: boolean) {
  if (!props.policy) return
  toggleField(props.policy.id, { doctorId, field: 'hasContract', value })
  markDirty()
}

function handleToggleDocumentation(doctorId: number, value: boolean) {
  if (!props.policy) return
  toggleField(props.policy.id, { doctorId, field: 'hasDocumentation', value })
  markDirty()
}

function handleRemoveDoctor(doctorId: number) {
  doctorToRemove.value = doctorId
  showRemoveConfirm.value = true
}

async function confirmRemoveDoctor() {
  if (doctorToRemove.value == null || !props.policy) return
  const doctorId = doctorToRemove.value
  markDirty()
  showRemoveConfirm.value = false
  doctorToRemove.value = null
  try {
    await store.removeDoctor(props.policy.id, doctorId)
  } catch (e) {
    notifyError(e, 'Erro ao remover médico da política.')
  }
}

function cancelRemoveDoctor() {
  showRemoveConfirm.value = false
  doctorToRemove.value = null
}

function validateForm(): string | null {
  if (!policyName.value.trim()) return 'Informe o nome da política.'
  if (!startDate.value) return 'Informe a data de início da vigência.'
  if (!isIndefiniteEnd.value && !endDate.value)
    return 'Informe a data de fim da vigência ou marque como indeterminado.'
  return null
}

async function updateExistingPolicy(): Promise<void> {
  if (!props.policy || !props.scheduleId || !props.linkId) {
    throw new Error('policy, scheduleId e linkId são obrigatórios para edição')
  }

  await Network.qualificationDocumentalPolicy.updatePolicy(props.policy.id, {
    name: policyName.value.trim()
  })

  await Network.qualificationDocumentalPolicy.updateScheduleLink(props.scheduleId, props.linkId, {
    startDate: startDate.value || undefined,
    endDate: isIndefiniteEnd.value ? null : endDate.value || undefined
  })

  toast.success('Política atualizada com sucesso.')
}

async function handleSave() {
  const error = validateForm()
  if (error) {
    toast.error(error)
    return
  }

  try {
    isSaving.value = true
    await updateExistingPolicy()
    emit('saved')
    emit('close')
  } catch (e) {
    notifyError(e, 'Não foi possível salvar a política.')
  } finally {
    isSaving.value = false
  }
}
</script>
